
import { defineComponent, ref, computed, onMounted } from "vue";
import VueApexCharts from "vue3-apexcharts";
import ApiService from "@/core/services/ApiService";
import { Endpoints } from "@/api/ApiEndpoints";

export default defineComponent({
  name: "MonitoringPage",
  components: {
    apexchart: VueApexCharts,
  },
  setup() {
    const timeRanges = ["1h", "6h", "24h", "7d"];
    const selectedRange = ref("24h");
    const loading = ref(false);
    const lastUpdated = ref("");

    const healthData = ref<any>(null);
    const exceptionsData = ref<any>(null);
    const planData = ref<any>(null);

    const loadHealth = async () => {
      try {
        const res = await ApiService.get(Endpoints.MONITORING_HEALTH, "", { timeRange: selectedRange.value });
        healthData.value = res?.data ?? null;
      } catch (e) {
        console.error("Failed to load health", e);
      }
    };

    const loadExceptions = async () => {
      try {
        const res = await ApiService.get(Endpoints.MONITORING_EXCEPTIONS, "", { timeRange: selectedRange.value });
        exceptionsData.value = res?.data ?? null;
      } catch (e) {
        console.error("Failed to load exceptions", e);
      }
    };

    const loadPlan = async () => {
      try {
        const res = await ApiService.get(Endpoints.MONITORING_PLAN, "", { timeRange: selectedRange.value });
        planData.value = res?.data ?? null;
      } catch (e) {
        console.error("Failed to load plan", e);
      }
    };

    const refreshAll = async () => {
      loading.value = true;
      await Promise.all([loadHealth(), loadExceptions(), loadPlan()]);
      lastUpdated.value = new Date().toLocaleTimeString("el-GR");
      loading.value = false;
    };

    const selectRange = (range: string) => {
      selectedRange.value = range;
      refreshAll();
    };

    onMounted(() => refreshAll());

    // Health summary cards
    const healthCards = computed(() => {
      const h = healthData.value;
      if (!h) return [];
      return [
        {
          label: "Total Requests",
          value: h.totalRequests?.toLocaleString("el-GR") ?? "0",
          icon: "bi bi-globe",
        },
        {
          label: "Failed Requests",
          value: h.failedRequests?.toLocaleString("el-GR") ?? "0",
          sub: `${h.failureRate ?? 0}% failure rate`,
          icon: "bi bi-exclamation-triangle",
        },
        {
          label: "Avg Response",
          value: `${h.avgResponseMs ?? 0} ms`,
          icon: "bi bi-speedometer2",
        },
        {
          label: "Memory",
          value: h.memoryMB != null ? `${h.memoryMB} MB` : "N/A",
          sub: h.cpuPercent != null ? `CPU: ${h.cpuPercent}%` : undefined,
          icon: "bi bi-memory",
        },
      ];
    });

    // CPU & Memory from plan metrics
    const cpuMemMetrics = computed(() => {
      if (!planData.value?.metrics) return [];
      return planData.value.metrics.filter(
        (m: any) => m.name === "CpuPercentage" || m.name === "MemoryPercentage"
      );
    });

    // Failure Rate radial gauge
    const failureRateOptions = computed(() => ({
      chart: { height: 280 },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          hollow: { size: "60%" },
          track: { background: "#1e2a45" },
          dataLabels: {
            name: { show: true, color: "#ccc", fontSize: "14px", offsetY: -10 },
            value: { show: true, color: "#fff", fontSize: "28px", formatter: (val: number) => `${val}%` },
          },
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "horizontal",
          colorStops: [
            { offset: 0, color: "#00E396" },
            { offset: 50, color: "#FEB019" },
            { offset: 100, color: "#F50242" },
          ],
        },
      },
      labels: ["Failure Rate"],
    }));

    // Requests pie
    const requestsPieSeries = computed(() => {
      const h = healthData.value;
      if (!h) return [0, 0];
      const success = (h.totalRequests ?? 0) - (h.failedRequests ?? 0);
      return [success, h.failedRequests ?? 0];
    });

    const requestsPieOptions = computed(() => ({
      labels: ["Successful", "Failed"],
      colors: ["#00E396", "#F50242"],
      legend: { labels: { colors: "#fff" }, position: "bottom" },
      tooltip: { theme: "dark" },
    }));

    // Gauge for CPU/Memory
    const gaugeOptions = (name: string, status: string) => {
      const color = status === "red" ? "#F50242" : status === "yellow" ? "#FEB019" : "#00E396";
      return {
        chart: { height: 180 },
        plotOptions: {
          radialBar: {
            hollow: { size: "55%" },
            track: { background: "#1e2a45" },
            dataLabels: {
              name: { show: false },
              value: { show: true, color: "#fff", fontSize: "18px", formatter: (val: number) => `${val}%` },
            },
          },
        },
        fill: { colors: [color] },
        labels: [name],
      };
    };

    // Helpers
    const formatMetricName = (name: string) => {
      const map: Record<string, string> = {
        CpuPercentage: "CPU",
        MemoryPercentage: "Memory",
        DiskQueueLength: "Disk Queue",
        HttpQueueLength: "HTTP Queue",
        BytesReceived: "Data In",
        BytesSent: "Data Out",
      };
      return map[name] || name;
    };

    const formatDate = (value: string | null) => {
      if (!value) return "—";
      const d = new Date(value);
      return isNaN(d.getTime()) ? "—" : d.toLocaleString("el-GR");
    };

    const statusIcon = (status: string) => {
      if (status === "critical") return "bi bi-x-circle-fill text-danger";
      if (status === "warning") return "bi bi-exclamation-triangle-fill text-warning";
      return "bi bi-check-circle-fill text-success";
    };

    const statusTextClass = (status: string) => {
      if (status === "critical" || status === "red") return "text-danger";
      if (status === "warning" || status === "yellow") return "text-warning";
      return "text-success";
    };

    return {
      timeRanges,
      selectedRange,
      loading,
      lastUpdated,
      healthData,
      exceptionsData,
      planData,
      healthCards,
      cpuMemMetrics,
      failureRateOptions,
      requestsPieSeries,
      requestsPieOptions,
      gaugeOptions,
      formatMetricName,
      formatDate,
      statusIcon,
      statusTextClass,
      selectRange,
      refreshAll,
    };
  },
});
